import './leasing-offers.scss';

import { useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { TLeasingOrderResponse } from '@/components/App/Leasing/types';
import { useAppSelector } from '@/store';

import { DraftOffer } from '../../../../entities';
import { IeLeasingOrderContext } from '../../../IeOrder';
import { useSelectedOffer } from '../../../hooks';
import { LeasingDeclinedItem, LeasingWaitingItem } from '../../entities';
import { LeasingApprovedItem } from '../../features';
import { LeasingOffersError } from '../../ui';
import { useSubmitOffer } from './hooks';

const LeasingOffers = () => {
  const { request } = useAppSelector((state) => state.ieLeasingOrder);

  const { isActionsDisabled } = useContext(IeLeasingOrderContext);

  const { handleSubmitOffer, submitLoadingOfferIds } = useSubmitOffer();

  const { selectedOffer } = useSelectedOffer();

  const isHaveFinanced: boolean = useMemo(() => {
    return !!request?.responses.find((item) => item.status.code === 'financed');
  }, [request?.responses]);

  const {
    approved: approvedOffers,
    declined: declinedOffers,
    waiting: waitingOffers,
    notSent: notSentOffers
  } = useMemo(() => {
    const leasingOffersMap: Record<
      'approved' | 'declined' | 'waiting' | 'notSent',
      TLeasingOrderResponse[]
    > = {
      approved: [],
      declined: [],
      waiting: [],
      notSent: []
    };

    if (!request) return leasingOffersMap;

    request.responses.forEach((response) => {
      const statusCode = response.status?.code;

      if (!statusCode) return;

      switch (statusCode) {
        case 'accepted_alternative':
        case 'approved':
        case 'client_accepted':
        case 'financed':
        case 'waiting_of_financing':
          leasingOffersMap.approved.push(response);
          break;
        case 'bank_response_timeout':
        case 'cancelled':
        case 'declined':
        case 'error':
          leasingOffersMap.declined.push(response);
          break;
        case 'waiting_bank_response':
        case 'on_review':
          leasingOffersMap.waiting.push(response);
          break;
        case 'not_sent':
          leasingOffersMap.notSent.push(response);
          break;
        default:
          break;
      }
    });

    return leasingOffersMap;
  }, [request]);

  const onlyErrorResponses: boolean =
    declinedOffers.length > 0 &&
    approvedOffers.length === 0 &&
    waitingOffers.length === 0;

  return (
    <div className="ie-offers">
      {approvedOffers.length === 0 &&
        waitingOffers.length === 0 &&
        declinedOffers.length === 0 && (
          <div className="ie-offers__empty">Нет предложений</div>
        )}

      {approvedOffers.map((leasing) => (
        <ErrorBoundary
          FallbackComponent={(props) => (
            <LeasingOffersError {...props} leasingOffer={leasing} />
          )}
          key={leasing.uuid}
        >
          <LeasingApprovedItem leasingOffer={leasing} />
        </ErrorBoundary>
      ))}

      {waitingOffers.map((leasing) => (
        <LeasingWaitingItem leasingOffer={leasing} key={leasing.uuid} />
      ))}

      {notSentOffers.map((offer) => (
        <DraftOffer
          key={offer.uuid}
          uuid={offer.uuid}
          img={offer.leasingCompany.icon}
          name={offer.leasingCompany.name}
          status={{ text: offer.status.name, code: offer.status.code }}
          onSend={() => handleSubmitOffer(offer.uuid)}
          sendLoading={submitLoadingOfferIds.includes(offer.uuid)}
          isDisabled={isHaveFinanced}
          isAcceptButton={!isActionsDisabled}
          isHasAccepted={!!selectedOffer}
        />
      ))}

      {declinedOffers.length > 0 && (
        <div>
          {!onlyErrorResponses && (
            <h1 className="ie-offers__declined-title"></h1>
          )}
          {declinedOffers.map((leasing) => (
            <LeasingDeclinedItem leasingOffer={leasing} key={leasing.uuid} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LeasingOffers;
